import React, { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { getDashboardPath } from '../utils/authRoutes';
import { AlertCircle, CheckCircle2, Eye, EyeOff, FileText, Lock, Mail, MapPin, Phone, Shield, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { hasPasswordSpaces, isValidEmail, normalizeEmail } from '../utils/authForms';

const formatApiError = (error) => {
  if (!error.response || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return 'No se pudo conectar con el servidor.';
  }
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item) => item?.msg || JSON.stringify(item)).join(' ');
  return error.message || 'No se pudo completar la solicitud.';
};

const AdminAuthPage = () => {
  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const loginInFlightRef = useRef(false);
  const [message, setMessage] = useState(null);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerData, setRegisterData] = useState({
    name: '',
    last_name: '',
    cedula: '',
    address: '',
    age: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleAdminLogin = async (event) => {
    event.preventDefault();
    if (loginInFlightRef.current) return;
    setMessage(null);
    const cleanEmail = normalizeEmail(loginData.email);
    const cleanPassword = loginData.password.trim();
    if (!isValidEmail(cleanEmail)) {
      setMessage({ type: 'error', text: 'Ingresa un correo valido.' });
      return;
    }
    setLoginData((prev) => ({ ...prev, email: cleanEmail, password: cleanPassword }));
    loginInFlightRef.current = true;
    setLoading(true);

    try {
      const result = await login(cleanEmail, cleanPassword, 'admin');

      if (!result.success) {
        setMessage({ type: 'error', text: result.error });
        return;
      }

      if (result.data.role !== 'admin') {
        await logout();
        setMessage({ type: 'error', text: 'Esta cuenta no tiene permisos de administrador.' });
        return;
      }

      navigate(getDashboardPath(result.data), { replace: true });
    } catch (error) {
      console.error('Unexpected admin login error:', {
        name: error?.name || 'Error',
        message: error?.message || 'Unknown error'
      });
      setMessage({ type: 'error', text: 'No se pudo completar el inicio de sesion. Intenta de nuevo.' });
    } finally {
      loginInFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleAdminRegister = async (event) => {
    event.preventDefault();
    setMessage(null);
    const cleanEmail = normalizeEmail(registerData.email);

    if (!isValidEmail(cleanEmail)) {
      setMessage({ type: 'error', text: 'Ingresa un correo valido.' });
      return;
    }

    if (registerData.password.length < 8) {
      setMessage({ type: 'error', text: 'La contrasena debe tener al menos 8 caracteres.' });
      return;
    }
    if (hasPasswordSpaces(registerData.password) || hasPasswordSpaces(registerData.confirmPassword)) {
      setMessage({ type: 'error', text: 'La contrasena no debe contener espacios.' });
      return;
    }
    if (registerData.password !== registerData.confirmPassword) {
      setMessage({ type: 'error', text: 'Las contrasenas no coinciden.' });
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/auth/admin-register`, {
        email: cleanEmail,
        password: registerData.password,
        name: registerData.name.trim(),
        last_name: registerData.last_name.trim(),
        phone: registerData.phone,
        cedula: registerData.cedula,
        address: registerData.address,
        age: Number(registerData.age),
        reason: `Solicitud de acceso administrador de ${registerData.name} ${registerData.last_name}`.trim()
      });
      setMessage({
        type: 'success',
        text: 'Tu solicitud fue enviada. La cuenta debe ser aprobada por otro administrador. Revisa tu correo y permanece pendiente.'
      });
      setRegisterData({
        name: '',
        last_name: '',
        cedula: '',
        address: '',
        age: '',
        phone: '',
        email: '',
        password: '',
        confirmPassword: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: formatApiError(error) });
    } finally {
      setLoading(false);
    }
  };

  const updateLogin = (field, value) => setLoginData((prev) => ({ ...prev, [field]: value }));
  const updateRegister = (field, value) => setRegisterData((prev) => ({ ...prev, [field]: value }));

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 relative overflow-hidden">
      <div className="mesh-glow-purple" style={{ top: '10%', right: '5%' }} />
      <div className="mesh-glow-blue" style={{ bottom: '10%', left: '5%' }} />

      <div className="w-full max-w-xl relative z-10">
        <div className="glass-card p-8 rounded-3xl">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-[#FF007F] to-[#8A2BE2]">
              <Shield className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
                Acceso administradores
              </h1>
              <p className="text-sm text-[#9CA3AF]">Area restringida de PROVI</p>
            </div>
          </div>

          {message && (
            <div className={`mb-5 p-4 rounded-xl border flex items-start gap-3 ${
              message.type === 'success'
                ? 'bg-green-500/10 border-green-500/20'
                : 'bg-red-500/10 border-red-500/20'
            }`}>
              {message.type === 'success'
                ? <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                : <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />}
              <p className={`text-sm ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                {message.text}
              </p>
            </div>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="bg-white/5 border border-white/10 p-1 grid w-full grid-cols-2 h-auto">
              <TabsTrigger value="login" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF007F] data-[state=active]:to-[#8A2BE2] rounded-lg">
                Iniciar sesion
              </TabsTrigger>
              <TabsTrigger value="register" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#FF007F] data-[state=active]:to-[#8A2BE2] rounded-lg">
                Solicitar cuenta
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleAdminLogin} className="space-y-5">
                <div>
                  <Label className="text-white mb-2 block">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <Input
                      name="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={loginData.email}
                      onChange={(e) => updateLogin('email', e.target.value)}
                      required
                      className="pl-11 bg-white/5 border-white/10 rounded-xl text-white"
                      placeholder="admin@provi.com"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white mb-2 block">Contrasena</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                    <Input
                      name="password"
                      type={showLoginPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={loginData.password}
                      onChange={(e) => updateLogin('password', e.target.value)}
                      required
                      className="pl-11 pr-11 bg-white/5 border-white/10 rounded-xl text-white"
                      placeholder="********"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword((current) => !current)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF] hover:text-white"
                      aria-label={showLoginPassword ? 'Ocultar contrasena' : 'Mostrar contrasena'}
                    >
                      {showLoginPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full gradient-button py-6 rounded-xl text-white font-medium">
                  {loading ? 'Verificando...' : 'Iniciar sesion como administrador'}
                </Button>
                <div className="text-right">
                  <Link to="/forgot-password?role=admin" className="text-xs text-[#9CA3AF] underline hover:text-white">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleAdminRegister} className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field icon={User} label="Nombres" value={registerData.name} onChange={(value) => updateRegister('name', value)} required />
                  <Field icon={User} label="Apellidos" value={registerData.last_name} onChange={(value) => updateRegister('last_name', value)} required />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field icon={FileText} label="Cedula" value={registerData.cedula} onChange={(value) => updateRegister('cedula', value)} required />
                  <Field icon={User} label="Edad" type="number" value={registerData.age} onChange={(value) => updateRegister('age', value)} required />
                </div>
                <Field icon={MapPin} label="Direccion" value={registerData.address} onChange={(value) => updateRegister('address', value)} required />
                <Field icon={Phone} label="Numero de telefono" name="phone" type="tel" autoComplete="tel" value={registerData.phone} onChange={(value) => updateRegister('phone', value)} required />
                <Field icon={Mail} label="Correo electronico" name="email" type="email" inputMode="email" autoComplete="email" value={registerData.email} onChange={(value) => updateRegister('email', value)} required />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PasswordField icon={Lock} label="Contrasena" name="password" value={registerData.password} onChange={(value) => updateRegister('password', value)} visible={showRegisterPassword} setVisible={setShowRegisterPassword} required />
                  <PasswordField icon={Lock} label="Confirmar contrasena" name="confirmPassword" value={registerData.confirmPassword} onChange={(value) => updateRegister('confirmPassword', value)} visible={showConfirmPassword} setVisible={setShowConfirmPassword} required />
                </div>
                <Textarea
                  value={`Solicitud de acceso administrador para ${registerData.name || 'nuevo usuario'} ${registerData.last_name || ''}`.trim()}
                  readOnly
                  className="bg-white/5 border-white/10 rounded-xl text-[#9CA3AF]"
                />
                <Button type="submit" disabled={loading} className="w-full gradient-button py-6 rounded-xl text-white font-medium">
                  {loading ? 'Enviando...' : 'Enviar solicitud de administrador'}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-[#9CA3AF] hover:text-white transition-colors duration-300">
            Volver al login
          </Link>
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, name, type = 'text', inputMode, autoComplete, value, onChange, required = false }) => (
  <div>
    <Label className="text-white mb-2 block">{label}</Label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
      <Input
        name={name}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="pl-11 bg-white/5 border-white/10 rounded-xl text-white placeholder:text-[#9CA3AF]"
      />
    </div>
  </div>
);

const PasswordField = ({ icon: Icon, label, name, value, onChange, visible, setVisible, required = false }) => (
  <div>
    <Label className="text-white mb-2 block">{label}</Label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
      <Input
        name={name}
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="pl-11 pr-11 bg-white/5 border-white/10 rounded-xl text-white placeholder:text-[#9CA3AF]"
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF] hover:text-white"
        aria-label={visible ? 'Ocultar contrasena' : 'Mostrar contrasena'}
      >
        {visible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  </div>
);

export default AdminAuthPage;
